/*
 * Shows the price after Shopify automatic discounts on the product page.
 *
 * Liquid cannot see automatic discounts before the product is in a cart, so
 * this simulates one: it creates a throwaway one-line cart through the
 * Storefront API (tokenless access, no key needed), reads the discount
 * allocations and rewrites the main price block with the theme's own markup.
 *
 * - The throwaway cart is separate from the visitor's cart: the cart count,
 *   the drawer and the add-to-cart pixel events are not touched.
 * - Only allocations targeting the line item are used, so free-shipping
 *   discounts never lower the displayed price.
 * - Quantity is 1, so discounts that need a minimum quantity or subtotal do
 *   not show — correct, since a single unit would not get them either.
 * - Discounts restricted to customer segments do not show: the simulated cart
 *   has no customer.
 * - Any failure leaves the server-rendered price untouched.
 */
(() => {
  const API_URL = '/api/2026-07/graphql.json';

  const QUERY = `mutation SimulateAutomaticDiscount($lines: [CartLineInput!]!, $country: CountryCode!)
  @inContext(country: $country) {
    cartCreate(input: { lines: $lines, buyerIdentity: { countryCode: $country } }) {
      cart {
        discountAllocations { targetType discountedAmount { amount } }
        lines(first: 1) {
          nodes {
            cost {
              amountPerQuantity { amount }
              compareAtAmountPerQuantity { amount }
            }
            discountAllocations { targetType discountedAmount { amount } }
          }
        }
      }
      userErrors { message }
    }
  }`;

  // One simulation per variant per page view.
  const cache = new Map();

  const toCents = (amount) => Math.round(parseFloat(amount) * 100);

  const lineItemDiscountCents = (allocations) =>
    (allocations || [])
      .filter((a) => a.targetType === 'LINE_ITEM')
      .reduce((sum, a) => sum + toCents(a.discountedAmount.amount), 0);

  function simulate(variantId, country) {
    const key = `${variantId}:${country}`;
    if (cache.has(key)) return cache.get(key);

    const request = fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          country,
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        },
      }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        const cart = json?.data?.cartCreate?.cart;
        const line = cart?.lines?.nodes?.[0];
        if (!line) return null;

        const price = toCents(line.cost.amountPerQuantity.amount);
        const compareAt = line.cost.compareAtAmountPerQuantity
          ? toCents(line.cost.compareAtAmountPerQuantity.amount)
          : 0;
        // With a single line, a cart-level discount on products applies entirely to it.
        const discount =
          lineItemDiscountCents(line.discountAllocations) + lineItemDiscountCents(cart.discountAllocations);

        if (discount <= 0 || discount >= price) return null;
        return { price, compareAt, final: price - discount };
      })
      .catch(() => null);

    cache.set(key, request);
    return request;
  }

  function formatMoney(cents, format) {
    const helper = window.Alpine?.store?.('xHelper');
    if (format && helper?.formatMoney) return helper.formatMoney(cents, format).replace(/<[^>]*>/g, '');
    return (cents / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  }

  function render(container, result, format) {
    const priceEl = container.querySelector('.main-product-price');
    if (!priceEl) return;

    // Strike the highest reference price already shown, so an existing
    // compare-at price is not replaced by a lower one.
    const reference = Math.max(result.price, result.compareAt);
    const percentage = Math.round(((reference - result.final) * 100) / reference);

    const strike = document.createElement('s');
    strike.className = 'cap rtl:inline-block price-compare rtl:leading-tight text-[80%]';
    strike.textContent = formatMoney(reference, format);

    const sale = document.createElement('span');
    sale.className = 'price-sale selection:bg-text-[rgb(var(--colors-price-sale),0.2)] ml-1 rtl:mr-1 rtl:ml-0';
    sale.textContent = formatMoney(result.final, format);

    const badge = document.createElement('span');
    badge.className = 'price-discount-badge ml-1 rtl:ml-0 rtl:mr-1';
    badge.style.cssText =
      'display:inline-block;background:rgb(var(--colors-price-sale));color:#fff;font-size:11px;font-weight:600;line-height:1;padding:3px 6px;border-radius:4px;vertical-align:middle;white-space:nowrap;';
    badge.textContent = `-${percentage}%`;

    // Keep the hidden target-price / target-compare-at-price nodes other scripts read.
    priceEl
      .querySelectorAll(':scope > s, :scope > .price-sale, :scope > .price-discount-badge, :scope > p.price')
      .forEach((node) => node.remove());
    // Same whitespace as price.liquid: a space between the struck and the sale price, none before the badge.
    priceEl.append(strike, ' ', sale, badge);
    priceEl.dataset.automaticDiscount = 'applied';
  }

  function update(container, variantId) {
    if (!variantId) return;
    const { country, moneyFormat } = container.dataset;
    simulate(variantId, country).then((result) => {
      // Ignore a late response for a variant that is no longer selected.
      if (result && String(container.dataset.variantId) === String(variantId)) {
        render(container, result, moneyFormat);
      }
    });
  }

  const selectorFor = (sectionId) => `[data-automatic-discount-price][data-section-id="${sectionId}"]`;
  const listening = new Set();

  function init() {
    document.querySelectorAll('[data-automatic-discount-price]').forEach((container) => {
      const { sectionId } = container.dataset;
      update(container, container.dataset.variantId);

      if (listening.has(sectionId)) return;
      listening.add(sectionId);

      // Section-scoped event from variant-select.js, dispatched after the theme has
      // re-rendered the price. The global eurus:variant:change is not used because
      // product cards and the quick view emit it too. The container is looked up
      // again because swapping to a sibling product replaces the section markup.
      document.addEventListener(`eurus:product-card-variant-select:updated:${sectionId}`, (event) => {
        const variantId = event.detail?.currentVariant?.id;
        const current = document.querySelector(selectorFor(sectionId));
        if (!variantId || !current) return;
        current.dataset.variantId = variantId;
        update(current, variantId);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
